import React, { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import { Button } from 'components/Button'
import { Container } from 'components/Container'
import { InputHookForm } from 'components/InputHookForm'
import { Spinner } from 'components/Spinner'

import { resetErrorAction } from 'root-redux/actions/common'
import {
  selectActionList,
  selectAppName,
  selectError,
} from 'root-redux/selects/common'
import { TAppDispatch } from 'root-redux/store/store'

import { NAME_VALIDATION } from 'modules/contactForm/constants'
import {
  SEND_FORM,
  removeAllFilesAction,
  sendCustomFormAction,
  setIsModalShownAction,
} from 'modules/contactForm/redux'

import { APP_BG_COLORS, READABLE_APP_NAMES } from 'root-constants'

import { StyledWithdrawContactForm as S } from './WithdrawContactForm.styles'
import { WithdrawModal } from './WithdrawModal'

const WITHDRAW_TOPIC = 'Withdraw from Contract'

interface IWithdrawForm {
  name: string
  surname: string
  email: string
  contract: string
}

export const WithdrawContactForm: React.FC = () => {
  const dispatch: TAppDispatch = useDispatch()
  const appName = useSelector(selectAppName)
  const error = useSelector(selectError)
  const fetchingActionsList = useSelector(selectActionList)

  const urlParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  )

  const emailFromUrl = useMemo(
    () => urlParams.get('email')?.replace(/"/g, '') ?? '',
    [urlParams],
  )

  const contract = useMemo(
    () =>
      urlParams.get('contract')?.replace(/"/g, '') ||
      `${READABLE_APP_NAMES[appName]} Premium Subscription`,
    [urlParams, appName],
  )

  const isFormSendingFromStore = useMemo(
    () => fetchingActionsList.includes(SEND_FORM),
    [fetchingActionsList],
  )

  const {
    register,
    getValues,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<IWithdrawForm>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      surname: '',
      email: emailFromUrl,
      contract,
    },
  })

  const onSubmit = useCallback(
    (data: IWithdrawForm) => {
      dispatch(
        sendCustomFormAction(
          {
            name: `${data.name} ${data.surname}`.trim(),
            email: data.email,
            topic: WITHDRAW_TOPIC,
            request: `Contract: ${contract}`,
          },
          ['withdrawal'],
          'formError',
        ),
      )
    },
    [dispatch, contract],
  )

  const handleCloseModal = useCallback(() => {
    dispatch(setIsModalShownAction(false))
    dispatch(removeAllFilesAction())

    if (window?.webkit?.messageHandlers?.jsHandler) {
      window.webkit.messageHandlers.jsHandler.postMessage('Popup was closed')
    }

    if (window?.JSBridge?.postMessage) {
      window.JSBridge.postMessage('Popup was closed')
    }

    if (!error) {
      reset()
      return
    }
    dispatch(resetErrorAction())
  }, [dispatch, error, reset])

  return (
    <S.Wrapper>
      <S.Header />
      {isFormSendingFromStore && <Spinner />}
      <Container>
        <S.Title>Withdraw from Contract</S.Title>
        <S.Form
          onSubmit={handleSubmit(onSubmit)}
          backgroundColor={APP_BG_COLORS[appName]}
        >
          <S.FormInputsWrapper>
            <InputHookForm
              register={register('name', {
                required: 'Name is required',
                validate: { ...NAME_VALIDATION },
              })}
              spellCheck={false}
              labelName="Your name *"
              isValid={!errors.name}
              validationText={errors.name?.message as string}
              marginBottom={24}
            />
            <InputHookForm
              register={register('surname', {
                required: 'Surname is required',
                validate: { ...NAME_VALIDATION },
              })}
              spellCheck={false}
              labelName="Your surname *"
              isValid={!errors.surname}
              validationText={errors.surname?.message as string}
              marginBottom={24}
            />
            <InputHookForm
              register={register('email')}
              labelName="Your email"
              isValid
              marginBottom={24}
              disabled
            />
            <InputHookForm
              register={register('contract')}
              labelName="Contract"
              isValid
              marginBottom={24}
              disabled
            />
          </S.FormInputsWrapper>

          <Button type="submit" disabled={!isValid || isFormSendingFromStore}>
            Submit
          </Button>
        </S.Form>
      </Container>
      <WithdrawModal
        name={getValues('name')}
        surname={getValues('surname')}
        email={getValues('email')}
        onClose={handleCloseModal}
      />
    </S.Wrapper>
  )
}
